// Racket Lab Admin — Enum → label-key mappings (i18n QUICK 1.A scaffolding).
//
// SCAFFOLDING ONLY. No JSX consumes this file yet — wiring happens in
// QUICK 1.B (RoleBadge, EnvelopeStateBadge, FlagBadge, etc.).
//
// Purpose: keep technical enum values (payload-stable, never translated)
// separate from their human-visible labels (translated via i18n).
//
// Pattern of use (Q1.B and later):
//
//   import { ROLE_LABEL_KEY } from "@/i18n/racketLabMaps";
//   import { useLang } from "@/i18n";
//   const { t } = useLang();
//   <span>{t(ROLE_LABEL_KEY[role] || ROLE_LABEL_KEY.__unknown)}</span>
//
// HARD RULES:
//   1. Keys here are the EXACT enum string values returned by the backend
//      (never translate them, never alter casing). Same string lands in
//      Mongo documents and audit logs.
//   2. Values point to dotted-path keys consumed by i18n `t()`.
//   3. `__unknown` fallback present in every map for defensive rendering.
//   4. Sources of truth (DO NOT EDIT — mirror the backend enums):
//        - Roles:                backend/app/modules/racket_lab/promotion.py:67-87
//        - QR token kinds:       backend/app/modules/qr_tokens/models.py:57-66
//        - Ticket statuses:      backend/app/modules/racket_lab_tickets/models.py
//        - Envelope statuses:    backend/app/modules/notifications/models.py
//        - Legacy buckets:       backend/app/modules/racket_lab/seed_membership_intents.py
//        - Scan audit results:   backend/app/modules/qr_tokens/resolve.py
//      Adding a new value backend-side REQUIRES adding it here AND in en.js/es.js.

// ---------------------------------------------------------------------------
// Roles (9) — ALLOWED_RUNTIME_ROLES ∪ BLOCKED_FUTURE_ROLES
// ---------------------------------------------------------------------------
export const ROLE_LABEL_KEY = {
  admin: "racketLab.roles.admin",
  staff: "racketLab.roles.staff",
  shop_manager: "racketLab.roles.shop_manager",
  operator: "racketLab.roles.operator",
  racket_technician: "racketLab.roles.racket_technician",
  head_coach: "racketLab.roles.head_coach",
  customer: "racketLab.roles.customer",
  guardian: "racketLab.roles.guardian",
  sponsor: "racketLab.roles.sponsor",
  __unknown: "racketLab.roles.unknown",
};

// ---------------------------------------------------------------------------
// QR token kinds (8) — QrTokenKind Literal
// ---------------------------------------------------------------------------
export const TOKEN_KIND_LABEL_KEY = {
  worker_personal: "racketLab.token_kinds.worker_personal",
  worker_self_open: "racketLab.token_kinds.worker_self_open",
  customer_checkin: "racketLab.token_kinds.customer_checkin",
  customer_handover: "racketLab.token_kinds.customer_handover",
  equipment: "racketLab.token_kinds.equipment",
  shop: "racketLab.token_kinds.shop",
  ticket: "racketLab.token_kinds.ticket",
  event_checkin: "racketLab.token_kinds.event_checkin",
  __unknown: "racketLab.token_kinds.unknown",
};

// ---------------------------------------------------------------------------
// Ticket statuses — RL-A.6 canonical + Q1.A scaffolding labels.
//
// SS-1 (Feb 2026): backend RL-A.6 is the source of truth. The
// canonical enum is `draft / checked_in / assigned / in_progress /
// pending_customer_approval / ready_for_pickup / closed / cancelled /
// deleted`. We KEEP the legacy R3 keys below for backwards-compatibility
// with any legacy `/racket-lab/service-tickets` consumer; new admin
// surfaces must drive the stepper from the RL-A.6 enum and the
// `GET /admin/racket-lab/tickets/{id}/allowed-actions` endpoint.
// ---------------------------------------------------------------------------
export const TICKET_STATUS_LABEL_KEY = {
  // RL-A.6 canonical
  draft: "racketLab.ticket_status.draft",
  checked_in: "racketLab.ticket_status.checked_in",
  assigned: "racketLab.ticket_status.assigned",
  in_progress: "racketLab.ticket_status.in_progress",
  pending_customer_approval: "racketLab.ticket_status.pending_customer_approval",
  ready_for_pickup: "racketLab.ticket_status.ready_for_pickup",
  closed: "racketLab.ticket_status.closed",
  cancelled: "racketLab.ticket_status.cancelled",
  deleted: "racketLab.ticket_status.deleted",
  // Legacy R3 / Q1.A scaffolding (kept for backwards-compat)
  received: "racketLab.ticket_status.received",
  ready: "racketLab.ticket_status.ready",
  handed_over: "racketLab.ticket_status.handed_over",
  on_hold: "racketLab.ticket_status.on_hold",
  requested: "racketLab.ticket_status.requested",
  accepted: "racketLab.ticket_status.accepted",
  pickup: "racketLab.ticket_status.pickup",
  diagnosis: "racketLab.ticket_status.diagnosis",
  pending_approval: "racketLab.ticket_status.pending_approval",
  approved: "racketLab.ticket_status.approved",
  delivered: "racketLab.ticket_status.delivered",
  __unknown: "racketLab.ticket_status.unknown",
};

// ---------------------------------------------------------------------------
// Ticket actions (9) — backend per-action endpoints. Keys here are the
// SLUG used in the route (kebab-case). Source of truth: backend ticket
// transitions (Sub-sprint B `ACTION_ENDPOINT`).
// ---------------------------------------------------------------------------
export const TICKET_ACTION_LABEL_KEY = {
  "check-in": "racketLab.ticket_actions.check_in",
  "assign-operator": "racketLab.ticket_actions.assign_operator",
  "assign-machine": "racketLab.ticket_actions.assign_machine",
  "start": "racketLab.ticket_actions.start",
  "request-approval": "racketLab.ticket_actions.request_approval",
  "mark-approved": "racketLab.ticket_actions.mark_approved",
  "finish": "racketLab.ticket_actions.finish",
  "pickup": "racketLab.ticket_actions.pickup",
  "cancel": "racketLab.ticket_actions.cancel",
  __unknown: "racketLab.common.unknown",
};

// ---------------------------------------------------------------------------
// Notification envelope statuses (5)
// ---------------------------------------------------------------------------
export const ENVELOPE_STATUS_LABEL_KEY = {
  draft: "racketLab.envelope_status.draft",
  queued: "racketLab.envelope_status.queued",
  dispatched: "racketLab.envelope_status.dispatched",
  failed: "racketLab.envelope_status.failed",
  cancelled: "racketLab.envelope_status.cancelled",
  __unknown: "racketLab.envelope_status.unknown",
};

// ---------------------------------------------------------------------------
// Legacy membership buckets (3) — UPPER CASE on backend
// ---------------------------------------------------------------------------
export const MEMBERSHIP_BUCKET_LABEL_KEY = {
  BACKFILL: "racketLab.membership_buckets.backfill",
  SOFT_REVOKE: "racketLab.membership_buckets.soft_revoke",
  EXCLUDE: "racketLab.membership_buckets.exclude",
  __unknown: "racketLab.membership_buckets.unknown",
};

// ---------------------------------------------------------------------------
// Scan audit results (8 result + 7 metric labels)
// ---------------------------------------------------------------------------
export const SCAN_AUDIT_RESULT_LABEL_KEY = {
  granted: "racketLab.scan_audit.granted",
  denied_role: "racketLab.scan_audit.denied_role",
  denied_rate_limit: "racketLab.scan_audit.denied_rate_limit",
  denied_revoked: "racketLab.scan_audit.denied_revoked",
  denied_expired: "racketLab.scan_audit.denied_expired",
  denied_cross_org: "racketLab.scan_audit.denied_cross_org",
  denied_cross_shop: "racketLab.scan_audit.denied_cross_shop",
  denied_unknown: "racketLab.scan_audit.denied_unknown",
  __unknown: "racketLab.scan_audit.denied_unknown",
};

// ---------------------------------------------------------------------------
// Retention modes (3)
// ---------------------------------------------------------------------------
export const RETENTION_MODE_LABEL_KEY = {
  dry_run: "racketLab.retention.dry_run",
  apply: "racketLab.retention.apply",
  not_reversible: "racketLab.retention.not_reversible",
  __unknown: "racketLab.common.unknown",
};

// ---------------------------------------------------------------------------
// Ops actions (8)
// ---------------------------------------------------------------------------
export const OPS_ACTION_LABEL_KEY = {
  force_on: "racketLab.ops.gating.force_on.confirm",
  force_off: "racketLab.ops.gating.force_off.confirm",
  clear_override: "racketLab.ops.gating.clear_override.confirm",
  reset_health: "racketLab.ops.redis.reset",
  apply_promotion: "racketLab.ops.promote.apply",
  apply_rollback: "racketLab.ops.promote.rollback_apply",
  apply_classify: "racketLab.ops.legacy.apply_classify",
  apply_batch: "racketLab.ops.legacy.apply_batch",
  __unknown: "racketLab.common.unknown",
};

// ---------------------------------------------------------------------------
// Feature flag states (4)
// ---------------------------------------------------------------------------
export const FLAG_STATE_LABEL_KEY = {
  on: "racketLab.flag_states.on",
  off: "racketLab.flag_states.off",
  forced_on: "racketLab.flag_states.forced_on",
  forced_off: "racketLab.flag_states.forced_off",
  __unknown: "racketLab.flag_states.unknown",
};

// ---------------------------------------------------------------------------
// Dispatch banner flags
// ---------------------------------------------------------------------------
export const DISPATCH_STATE_LABEL_KEY = {
  provider_mock: "racketLab.dispatch.provider_mock",
  dispatch_false: "racketLab.dispatch.dispatch_false",
  dispatch_true: "racketLab.dispatch.dispatch_true",
  q28_false: "racketLab.dispatch.q28_false",
  q28_true: "racketLab.dispatch.q28_true",
  read_only: "racketLab.dispatch.read_only",
  internal_only: "racketLab.dispatch.internal_only",
  __unknown: "racketLab.common.unknown",
};

// ---------------------------------------------------------------------------
// Helper — defensive label-key resolver. Returns the mapped key OR the
// map's __unknown fallback. NEVER returns the raw enum value (that would
// surface technical strings in the UI).
//
// Usage:
//   const labelKey = labelKeyFor(ROLE_LABEL_KEY, user.role);
//   <span>{t(labelKey)}</span>
// ---------------------------------------------------------------------------
export function labelKeyFor(map, value) {
  if (map == null) return "racketLab.common.unknown";
  if (value != null && Object.prototype.hasOwnProperty.call(map, value)) {
    return map[value];
  }
  return map.__unknown || "racketLab.common.unknown";
}

// ---------------------------------------------------------------------------
// Default export — bundle of all maps. Convenient single-import pattern
// for components that need multiple maps.
// ---------------------------------------------------------------------------
const racketLabMaps = {
  ROLE_LABEL_KEY,
  TOKEN_KIND_LABEL_KEY,
  TICKET_STATUS_LABEL_KEY,
  TICKET_ACTION_LABEL_KEY,
  ENVELOPE_STATUS_LABEL_KEY,
  MEMBERSHIP_BUCKET_LABEL_KEY,
  SCAN_AUDIT_RESULT_LABEL_KEY,
  RETENTION_MODE_LABEL_KEY,
  OPS_ACTION_LABEL_KEY,
  FLAG_STATE_LABEL_KEY,
  DISPATCH_STATE_LABEL_KEY,
  labelKeyFor,
};

export default racketLabMaps;
